/**
 * Rolling stream of simulated incoming trade offers (Variant B only).
 *
 *  - Schedules a new offer every uniform-random delay in
 *    [shopOfferIntervalMinSec, shopOfferIntervalMaxSec].
 *  - Buffer caps at shopBufferMax; oldest drops when full.
 *  - Survives scene transitions: ShopService is a singleton stored in the
 *    Phaser game registry. RunnerScene and LauncherScene both pull from it.
 *  - Listeners (typically the active ShopScene) subscribe for buffer
 *    mutations so the UI re-renders without polling.
 *
 * Wallet-aware filtering happens at template selection: ShopService asks the
 * caller for the current Wallet so it can skip "buy a skill you already own"
 * / "sell a skill you don't have" generations. This means the offer stream
 * shape depends on what the player owns at generation time — not at view time.
 */

import Phaser from "phaser";

import { GAMEPLAY } from "../config";
import type { TradeOffer } from "../types";

import { offerAuthorSeed, type AuthorIdentityService } from "./AuthorIdentityService";
import { hashString, makeSeededRng } from "./seededRng";
import { pickTemplate, type OfferTemplate } from "./shopOfferTemplates";
import type { Wallet } from "./Wallet";

export type ShopListener = () => void;
export type ShopNewOfferListener = (offer: TradeOffer) => void;

export interface ShopDebugInfo {
  nextArrivalInMs: number;
  bufferSize: number;
  lastTemplateId: string | null;
}

const REGISTRY_KEY = "shopService";
/** How long after `arrivedAtMs` an offer stays in the buffer before it
 *  expires and quietly drops. 2 minutes (120 000 ms). */
export const OFFER_LIFETIME_MS = 120_000;

export interface ShopServiceDeps {
  game: Phaser.Game;
  identity: AuthorIdentityService;
  /** Returns the wallet to use for offer-eligibility filtering at generation time. */
  getCurrentWallet: () => Wallet | undefined;
  /** Telemetry emit (Layer 2). */
  emitTelemetry: (layer: 1 | 2 | 3, tag: string, data?: Record<string, number | string | boolean>) => void;
  /** Current ms-since-session-start; provided by caller so we don't depend on a specific scene clock. */
  now: () => number;
}

export class ShopService {
  private readonly buffer: TradeOffer[] = [];
  private readonly listeners = new Set<ShopListener>();
  private readonly newOfferListeners = new Set<ShopNewOfferListener>();
  private nextOfferAtMs = 0;
  private offerCounter = 0;
  private lastTemplateId: string | null = null;
  private updateBound = false;
  private readonly rng = makeSeededRng(hashString(`shop:${Date.now()}`));

  constructor(private readonly deps: ShopServiceDeps) {
    this.scheduleNext();
    deps.game.events.on(Phaser.Core.Events.PRE_STEP, this.onPreStep, this);
    this.updateBound = true;
    // Seed the buffer with `shopBufferMax` offers so the trade board never
    // looks empty when the player first walks in. Cadence-driven arrivals
    // still ride on top — the first real one fires after the next-arrival
    // delay scheduled above. Generated offers carry the same now() timestamp
    // since they all "arrived" before the session started; the analyst
    // pipeline cares about relative order, not strict spacing here.
    this.prepopulate();
  }

  private prepopulate(): void {
    // Stagger arrivedAtMs across the past ~100 seconds so countdowns vary
    // out of the gate — some offers are near-expiry, others fresh. Buffer
    // ordering: index 0 = newest (top of stack), len-1 = oldest (bottom).
    const total = GAMEPLAY.shopBufferMax;
    const span = 100_000;
    const step = total > 1 ? span / (total - 1) : 0;
    const now = this.deps.now();
    for (let i = 0; i < total; i++) {
      if (!this.tryGenerate({ silent: true })) break;
    }
    // Each `unshift` reverses position relative to generation order, so we
    // assign timestamps AFTER the buffer is built. buffer[0] is the newest.
    this.buffer.forEach((offer, idx) => {
      offer.arrivedAtMs = now - idx * step;
    });
  }

  static getOrCreate(deps: ShopServiceDeps): ShopService {
    const existing = deps.game.registry.get(REGISTRY_KEY) as ShopService | undefined;
    if (existing) return existing;
    const inst = new ShopService(deps);
    deps.game.registry.set(REGISTRY_KEY, inst);
    return inst;
  }

  destroy(): void {
    if (this.updateBound) {
      this.deps.game.events.off(Phaser.Core.Events.PRE_STEP, this.onPreStep, this);
      this.updateBound = false;
    }
    this.listeners.clear();
  }

  // -- public API -----------------------------------------------------------

  get offers(): readonly TradeOffer[] {
    return this.buffer;
  }

  subscribe(fn: ShopListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Subscribe to "a fresh offer just arrived" events. Lets the scene
   *  animate / play a sound only for new arrivals, separately from
   *  generic buffer changes (which also fire on accept / expiry). */
  onNewOffer(fn: ShopNewOfferListener): () => void {
    this.newOfferListeners.add(fn);
    return () => this.newOfferListeners.delete(fn);
  }

  /** Force-arrive an offer now (used by manual debug). Returns the generated offer, if any. */
  generateNow(): TradeOffer | undefined {
    return this.tryGenerate();
  }

  /** Apply a successful trade. Mutates the wallet and removes the offer. Returns true on success. */
  acceptOffer(offerId: string, wallet: Wallet): boolean {
    const idx = this.buffer.findIndex((o) => o.id === offerId);
    if (idx < 0) return false;
    const offer = this.buffer[idx]!;
    // Convert cost + receive to wallet deltas and check affordability.
    const costDelta = resourcesAsCostDelta(offer.cost);
    if (!wallet.canAfford(costDelta)) {
      this.deps.emitTelemetry(2, "shop.trade.rejected", {
        offerId,
        templateId: offer.templateId,
        reason: "unaffordable",
      });
      return false;
    }
    // Apply: subtract cost, add receive.
    wallet.apply(invertDelta(costDelta));
    wallet.apply(resourcesAsReceiveDelta(offer.receive));
    this.buffer.splice(idx, 1);
    this.deps.emitTelemetry(2, "shop.trade.completed", {
      offerId,
      templateId: offer.templateId,
      costSummary: summariseResources(offer.cost),
      receiveSummary: summariseResources(offer.receive),
    });
    this.notify();
    return true;
  }

  debugInfo(): ShopDebugInfo {
    return {
      nextArrivalInMs: Math.max(0, this.nextOfferAtMs - this.deps.now()),
      bufferSize: this.buffer.length,
      lastTemplateId: this.lastTemplateId,
    };
  }

  // -- internals ------------------------------------------------------------

  private onPreStep(): void {
    const now = this.deps.now();
    // Expire offers older than OFFER_LIFETIME_MS. We don't tween or notify
    // per-expiry — the scene's countdown text already drives that visual.
    // We DO emit a single bulk notification if anything was dropped so the
    // scene re-renders the now-shifted buffer.
    let dropped = false;
    for (let i = this.buffer.length - 1; i >= 0; i--) {
      const offer = this.buffer[i]!;
      if (now - offer.arrivedAtMs >= OFFER_LIFETIME_MS) {
        this.buffer.splice(i, 1);
        dropped = true;
      }
    }
    if (dropped) this.notify();
    if (now >= this.nextOfferAtMs) {
      this.tryGenerate();
      this.scheduleNext();
    }
  }

  private scheduleNext(): void {
    const minMs = GAMEPLAY.shopOfferIntervalMinSec * 1000;
    const maxMs = GAMEPLAY.shopOfferIntervalMaxSec * 1000;
    const delay = minMs + this.rng.next() * (maxMs - minMs);
    this.nextOfferAtMs = this.deps.now() + delay;
  }

  private tryGenerate(opts?: { silent?: boolean }): TradeOffer | undefined {
    const wallet = this.deps.getCurrentWallet();
    const template = pickTemplate(this.rng, (t) => this.templateEligible(t, wallet));
    if (!template) return undefined;
    const built = template.build(this.rng);
    this.offerCounter += 1;
    const offerId = `offer-${String(this.offerCounter).padStart(4, "0")}`;
    const author = this.deps.identity.forSeed(offerAuthorSeed(offerId));
    const offer: TradeOffer = {
      id: offerId,
      arrivedAtMs: this.deps.now(),
      cost: built.cost,
      receive: built.receive,
      authorUsername: author.username,
      authorAvatarSeed: offerAuthorSeed(offerId),
      templateId: template.id,
    };
    this.buffer.unshift(offer);
    while (this.buffer.length > GAMEPLAY.shopBufferMax) this.buffer.pop();
    this.lastTemplateId = template.id;
    this.deps.emitTelemetry(2, "shop.offer.generated", {
      offerId,
      templateId: template.id,
      costSummary: summariseResources(offer.cost),
      receiveSummary: summariseResources(offer.receive),
      authorUsername: offer.authorUsername,
    });
    this.notify();
    // The arrive event fires only for live arrivals — not the
    // prepopulation pass, which uses `silent: true` so the scene
    // doesn't play 20 chime sounds + arrival animations on entry.
    if (!opts?.silent) {
      for (const fn of this.newOfferListeners) fn(offer);
    }
    return offer;
  }

  private templateEligible(t: OfferTemplate, wallet: Wallet | undefined): boolean {
    if (!wallet) {
      // No wallet context yet (very early boot) — only allow templates that
      // don't reference skill ownership.
      return !t.requiresSkill && !t.forbidsSkill;
    }
    if (t.requiresSkill) {
      for (const id of t.requiresSkill) if (!wallet.isSkillUnlocked(id)) return false;
    }
    if (t.forbidsSkill) {
      for (const id of t.forbidsSkill) if (wallet.isSkillUnlocked(id)) return false;
    }
    return true;
  }

  private notify(): void {
    for (const fn of this.listeners) fn();
  }
}

// -- helpers --------------------------------------------------------------

/** Aggregate a resource list into a positive-magnitude "cost" delta (resources the player gives up). */
function resourcesAsCostDelta(resources: readonly TradeOffer["cost"][number][]): import("../types").WalletDelta {
  return aggregate(resources);
}

/** Same as above but for the "receive" side (positive-magnitude). */
function resourcesAsReceiveDelta(resources: readonly TradeOffer["receive"][number][]): import("../types").WalletDelta {
  return aggregate(resources);
}

function aggregate(resources: readonly TradeOffer["cost"][number][]): import("../types").WalletDelta {
  const out: import("../types").WalletDelta = {};
  const addInv: { itemId: string }[] = [];
  const removeInv: string[] = [];
  const gain: import("../types").SkillId[] = [];
  for (const r of resources) {
    switch (r.kind) {
      case "geo":
        out.geo = (out.geo ?? 0) + r.amount;
        break;
      case "hp":
        out.hp = (out.hp ?? 0) + r.amount;
        break;
      case "feather":
        out.featherCharges = (out.featherCharges ?? 0) + r.amount;
        break;
      case "inventoryItem":
        addInv.push({ itemId: r.itemId });
        break;
      case "skill":
        gain.push(r.skillId);
        break;
    }
  }
  if (addInv.length) out.inventory = { add: addInv };
  if (removeInv.length) out.inventory = { ...(out.inventory ?? {}), remove: removeInv };
  if (gain.length) out.skills = { gain };
  return out;
}

/** Convert a positive-magnitude delta into its negation (apply as a debit). */
function invertDelta(delta: import("../types").WalletDelta): import("../types").WalletDelta {
  const out: import("../types").WalletDelta = {};
  if (delta.geo !== undefined) out.geo = -delta.geo;
  if (delta.hp !== undefined) out.hp = -delta.hp;
  if (delta.featherCharges !== undefined) out.featherCharges = -delta.featherCharges;
  if (delta.inventory?.add) out.inventory = { remove: delta.inventory.add.map((i) => i.itemId) };
  if (delta.skills?.gain) out.skills = { lose: delta.skills.gain };
  return out;
}

/** Short string summary for telemetry payloads (resources collapse to "2geo+1hp"). */
function summariseResources(resources: readonly TradeOffer["cost"][number][]): string {
  const parts: string[] = [];
  for (const r of resources) {
    switch (r.kind) {
      case "geo":
        parts.push(`${r.amount}geo`);
        break;
      case "hp":
        parts.push(`${r.amount}hp`);
        break;
      case "feather":
        parts.push(`${r.amount}feather`);
        break;
      case "inventoryItem":
        parts.push(r.itemId);
        break;
      case "skill":
        parts.push(`skill:${r.skillId}`);
        break;
    }
  }
  return parts.join("+");
}
